import { useState } from "react";
import { toast } from "react-toastify";
import { useCustomers } from "../../context/CustomerContext";
import { FiX } from "react-icons/fi";

export default function AddCustomer() {
    const emptyForm = {
        name: "",
        email: "",
        phone: "",
        address: "",
        status: "active",
        source: "",
    };

    const emptyErrors = {
        name: "",
        email: "",
        phone: "",
        address: "",
        status: "",
        source: "",
    };

    const { addCustomers } = useCustomers();

    const [forms, setForms] = useState([emptyForm]);
    const [errors, setErrors] = useState([emptyErrors]);
    const [loading, setLoading] = useState(false);
    const [resultBox, setResultBox] = useState(null);

    const validateField = (field, value, index) => {
        let message = "";

        if (!value.trim() && field !== "email" && field !== "address") {
            message = `${field} is required`;
        } else if (field === "phone" && !/^\d*$/.test(value)) {
            message = "Phone must contain digits only";
        } else if (field === "email") {
            const email = value.trim();
            for (let i = 0; i < forms.length; i++) {
                if (i !== index && forms[i].email.trim() === email) {
                    message = "Email must be unique";
                }
            }
        }

        const next = [...errors];
        next[index][field] = message;
        setErrors(next);

        return message === "";
    };

    const validateAllForms = () => {
        let isValid = true;
        const nextErrors = forms.map(() => ({ ...emptyErrors }));

        forms.forEach((form, idx) => {
            for (const key of Object.keys(form)) {
                const value = form[key].trim();

                if (!value && key !== "email" && key !== "address") {
                    nextErrors[idx][key] = `${key} is required`;
                    isValid = false;
                } else if (key === "phone" && !/^\d*$/.test(value)) {
                    nextErrors[idx][key] = "Phone must contain digits only";
                    isValid = false;
                }
            }
        });

        const seen = new Set();
        forms.forEach((form, idx) => {
            const email = form.email.trim();
            if (email && seen.has(email)) {
                nextErrors[idx].email = "Email must be unique";
                isValid = false;
            }
            seen.add(email);
        });

        setErrors(nextErrors);
        return isValid;
    };

    const handleFormChange = (index, field, value) => {
        const next = [...forms];
        next[index][field] = value;
        setForms(next);

        validateField(field, value, index);
    };

    const addNewForm = () => {
        const lastIndex = forms.length - 1;

        if (!validateSingleForm(lastIndex)) {
            toast.error("Please complete the current form before adding a new one.");
            return;
        }

        setForms([...forms, { ...emptyForm }]);
        setErrors([...errors, { ...emptyErrors }]);
    };

    const validateSingleForm = (index) => {
        let valid = true;
        const updatedErrors = { ...errors[index] };
        const form = forms[index];

        for (const key in form) {
            const value = form[key].trim();

            if (!value && key !== "email" && key !== "address") {
                updatedErrors[key] = `${key} is required`;
                valid = false;
            } else if (key === "phone" && !/^\d*$/.test(value)) {
                updatedErrors[key] = "Phone must contain digits only";
                valid = false;
            } else {
                updatedErrors[key] = "";
            }
        }

        const email = form.email.trim();
        for (let i = 0; i < forms.length; i++) {
            if (i !== index && forms[i].email.trim() === email) {
                updatedErrors.email = "Email must be unique";
                valid = false;
            }
        }

        const nextErrors = [...errors];
        nextErrors[index] = updatedErrors;
        setErrors(nextErrors);

        return valid;
    };

    const deleteForm = (index) => {
        setForms(forms.filter((_, i) => i !== index));
        setErrors(errors.filter((_, i) => i !== index));
        toast.success("Customer removed");
    };

    const handleSubmit = () => {
        if (!validateAllForms()) {
            toast.error("Please fix validation errors");
            return;
        }

        const finalPayload = forms.map(f => ({
            ...f,
            status: f.status === "active",
        }));

        toast.info(
            ({ closeToast }) => (
                <div className="flex flex-col gap-2">
                    <div className="font-medium">Confirm submission?</div>

                    <div className="flex justify-end gap-2">
                        <button
                            className="px-3 py-1 rounded bg-gray-300 text-black"
                            onClick={() => closeToast()}
                        >
                            Cancel
                        </button>

                        <button
                            className="px-3 py-1 rounded bg-blue-600 text-white"
                            onClick={async () => {
                                closeToast();
                                setLoading(true);

                                try {
                                    const response = await addCustomers(finalPayload);

                                    setResultBox({
                                        inserted: response.inserted || 0,
                                        skipped: response.skipped || []
                                    });

                                    toast.success("Customers added");

                                    setForms([{ ...emptyForm }]);
                                    setErrors([{ ...emptyErrors }]);
                                } catch (err) {
                                    toast.error("Failed to add customers");
                                } finally {
                                    setLoading(false);
                                }
                            }}
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            ),
            { autoClose: false }
        );
    };

    const clearAll = () => {
        toast.info(
            ({ closeToast }) => (
                <div className="flex flex-col gap-2">
                    <div className="font-medium">Clear all forms?</div>

                    <div className="flex justify-end gap-2">
                        <button
                            className="px-3 py-1 rounded bg-gray-300 text-black"
                            onClick={() => {
                                closeToast();
                            }}
                        >
                            Cancel
                        </button>

                        <button
                            className="px-3 py-1 rounded bg-red-600 text-white"
                            onClick={() => {
                                setForms([{ ...emptyForm }]);
                                setErrors([{ ...emptyErrors }]);
                                closeToast();
                                toast.success("All cleared");
                            }}
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            ),
            { autoClose: false }
        );
    };

    return (
        <div className="p-5 bg-white shadow rounded-lg">
            <h2 className="text-lg font-semibold mb-2">Create Customers</h2>

            {resultBox && (
                <div className="my-3 space-y-2 p-4 border rounded bg-gray-50 relative">
                    <button
                        className="absolute top-2 right-2 text-red-500 border bg-white p-1 rounded-md hover:bg-gray-50 hover:text-red-600 hover:shadow-md"
                        onClick={() => setResultBox(null)}
                    >
                        <FiX size={16} />
                    </button>

                    <h3 className="font-semibold text-base">Import Summary</h3>

                    <div><span className="font-semibold">Added Customers:</span> {resultBox.inserted}</div>
                    <div><span className="font-semibold">Skipped Customers:</span> {resultBox.skipped.length}</div>

                    {resultBox.skipped.length > 0 && (
                        <div>
                            <div className="font-semibold">Skipped List:</div>
                            <ul className="list-disc mt-1 text-sm text-gray-700">
                                {resultBox.skipped.map((s, i) => (
                                    <li key={i}>{s}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {forms.map((form, idx) => (
                <div key={idx} className="border p-3 mb-4 rounded-lg bg-gray-50">

                    <div className="flex justify-between items-center mb-2">
                        <h3 className="font-semibold text-lg">Customer #{idx + 1}</h3>
                        {forms.length > 1 && (
                            <button
                                onClick={() => deleteForm(idx)}
                                className="text-red-500 hover:underline"
                            >
                                Delete
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                        {/* Name */}
                        <div>
                            <input
                                value={form.name}
                                onChange={(e) =>
                                    handleFormChange(idx, "name", e.target.value)
                                }
                                placeholder="Name"
                                className="border rounded px-3 py-2 w-full"
                            />
                            {errors[idx].name && (
                                <p className="text-red-500 text-sm mt-1">{errors[idx].name}</p>
                            )}
                        </div>

                        {/* Email (optional) */}
                        <div>
                            <input
                                value={form.email}
                                onChange={(e) =>
                                    handleFormChange(idx, "email", e.target.value)
                                }
                                placeholder="Email (optional)"
                                className="border rounded px-3 py-2 w-full"
                            />
                            {errors[idx].email && (
                                <p className="text-red-500 text-sm mt-1">{errors[idx].email}</p>
                            )}
                        </div>

                        {/* Phone */}
                        <div>
                            <input
                                value={form.phone}
                                onChange={(e) =>
                                    handleFormChange(idx, "phone", e.target.value)
                                }
                                placeholder="Phone"
                                className="border rounded px-3 py-2 w-full"
                            />
                            {errors[idx].phone && (
                                <p className="text-red-500 text-sm mt-1">{errors[idx].phone}</p>
                            )}
                        </div>

                        {/* Address (optional) */}
                        <div>
                            <input
                                value={form.address}
                                onChange={(e) =>
                                    handleFormChange(idx, "address", e.target.value)
                                }
                                placeholder="Address (optional)"
                                className="border rounded px-3 py-2 w-full"
                            />
                            {errors[idx].address && (
                                <p className="text-red-500 text-sm mt-1">{errors[idx].address}</p>
                            )}
                        </div>

                        {/* Status */}
                        <div>
                            <select
                                value={form.status}
                                onChange={(e) => handleFormChange(idx, "status", e.target.value)}
                                className="border rounded px-3 py-2 w-full"
                            >
                                <option value="" disabled>Select Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                            {errors[idx].status && (
                                <p className="text-red-500 text-sm mt-1">{errors[idx].status}</p>
                            )}
                        </div>

                        {/* Source */}
                        <div>
                            <select
                                value={form.source}
                                onChange={(e) => handleFormChange(idx, "source", e.target.value)}
                                className="border rounded px-3 py-2 w-full"
                            >
                                <option value="" disabled>Select Source</option>
                                <option value="Web Portal">Web Portal</option>
                                <option value="Alice Receptions">Alice Receptions</option>
                                <option value="Alice Conference">Alice Conference</option>
                            </select>
                            {errors[idx].source && (
                                <p className="text-red-500 text-sm mt-1">{errors[idx].source}</p>
                            )}
                        </div>

                    </div>
                </div>
            ))}

            <button
                onClick={addNewForm}
                className="px-4 py-2 font-semibold rounded hover:shadow-md border text-sm bg-white hover:bg-gray-100 text-green-500 hover:text-green-600"
            >
                + Add Another Customer
            </button>

            <div className="mt-4 flex gap-2">
                <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-4 py-2 font-semibold rounded hover:shadow-md border text-sm bg-white hover:bg-gray-100 text-indigo-500 hover:text-indigo-600"
                >
                    {loading ? "Creating..." : "Create Customers"}
                </button>

                <button
                    onClick={clearAll}
                    className="px-4 py-2 font-semibold rounded hover:shadow-md border text-sm bg-white hover:bg-gray-100"
                >
                    Clear All
                </button>
            </div>
        </div>
    );
}
